// i18n is the language the web viewer speaks (spec 013). main.ts hands it the
// catalogs in i18n/common and i18n/web and the browser's languages; tests
// hand it the same files read from disk. Messages come from the catalog for
// the browser's language, then its base language, then English, one message
// at a time (013 FR-003). Dates, times, and numbers use the catalog's
// language with the browser's region (013 FR-012, 013 FR-013).
import { format, pseudo, type Args } from './icu.ts';

export type Catalog = Record<string, string>;

const catalogs = new Map<string, Catalog>();

export const PSEUDO = 'en-XA';

// state is the chosen catalog, the locale formats use, and whether the
// pseudo-language shows.
const state = { catalog: 'en', format: 'en-US', pseudo: false, device: 'en-US' };

// addCatalog merges one ARB file's messages into its language's catalog.
export function addCatalog(arb: Record<string, unknown>) {
  const lang = typeof arb['@@locale'] === 'string' ? (arb['@@locale'] as string) : 'en';
  const into = catalogs.get(lang) ?? {};
  for (const [key, value] of Object.entries(arb)) {
    if (key.startsWith('@') || typeof value !== 'string') continue;
    into[key] = value;
  }
  catalogs.set(lang, into);
}

// setLanguage picks the catalog for the first of tags Kehai has, in the
// browser's order; development builds accept the pseudo-language.
export function setLanguage(tags: readonly string[], development = false) {
  const first = tags[0] ?? 'en-US';
  state.device = first;
  state.pseudo = development && first.toLowerCase() === PSEUDO.toLowerCase();
  state.catalog = state.pseudo ? 'en' : (tags.map(match).find((m) => m !== null) ?? 'en');
  const region = state.pseudo ? 'US' : regionOf(first);
  state.format = region ? `${state.catalog}-${region}` : state.catalog;
}

// withScript adds the script a Chinese tag leaves out, from its region, as
// CLDR's likely subtags do: Traditional for Taiwan, Hong Kong, and Macau,
// Simplified elsewhere (013 FR-026).
export function withScript(tag: string): string {
  const parts = tag.toLowerCase().split('-');
  if (parts[0] !== 'zh' || parts[1]?.length === 4) return tag.toLowerCase();
  const script = ['tw', 'hk', 'mo'].includes(parts[1] ?? '') ? 'hant' : 'hans';
  return ['zh', script, ...parts.slice(1)].join('-');
}

function match(tag: string): string | null {
  const parts = withScript(tag).split('-');
  const keys = new Map([...catalogs.keys()].map((k) => [k.toLowerCase(), k]));
  return keys.get(parts.join('-')) ?? (parts.length > 2 ? keys.get(`${parts[0]}-${parts[1]}`) : undefined) ?? keys.get(parts[0]) ?? null;
}

function regionOf(tag: string): string | null {
  try {
    return new Intl.Locale(tag).region ?? null;
  } catch {
    return null;
  }
}

// acceptLanguage is the browser's own language tag, which the server writes
// its messages in (spec 013 FR-018).
export function acceptLanguage(): string {
  return state.device;
}

// language is the catalog's language, for <html lang>.
export function language(): string {
  return state.catalog;
}

// locale is the locale dates, times, and numbers are written in.
export function locale(): string {
  return state.format;
}

// t formats the message key with args in the current language.
export function t(key: string, args: Args = {}): string {
  const own = catalogs.get(state.catalog)?.[key];
  const baseLang = state.catalog.split('-')[0];
  const base = own === undefined ? catalogs.get(baseLang)?.[key] : undefined;
  const message = own ?? base ?? catalogs.get('en')?.[key];
  if (message === undefined) return key;
  const lang = own !== undefined ? state.catalog : base !== undefined ? baseLang : 'en';
  return format(state.pseudo ? pseudo(message) : message, lang, args, integer);
}

// plain turns CLDR's narrow no-break space before AM and PM into a plain
// space; browsers and Node disagree on it, and the apps write a plain one.
function plain(s: string): string {
  return s.replace(/\u202f/g, ' ');
}

// time is a clock time in the region's 12- or 24-hour clock.
export function time(at: Date, timeZone?: string): string {
  return plain(new Intl.DateTimeFormat(state.format, { hour: 'numeric', minute: '2-digit', timeZone }).format(at));
}

// hour is an hour alone, for chart axes: "9 PM" or "21".
export function hour(at: Date, timeZone?: string): string {
  return plain(new Intl.DateTimeFormat(state.format, { hour: 'numeric', timeZone }).format(at));
}

// weekday is a weekday's name, long or short.
export function weekday(at: Date, short = false, timeZone?: string): string {
  return new Intl.DateTimeFormat(state.format, { weekday: short ? 'short' : 'long', timeZone }).format(at);
}

// month is a month's name, long or short.
export function month(at: Date, short = false, timeZone?: string): string {
  return new Intl.DateTimeFormat(state.format, { month: short ? 'short' : 'long', timeZone }).format(at);
}

// monthDay is a day of a month, "Mar 4" or "4 Mar".
export function monthDay(at: Date, timeZone?: string): string {
  return plain(new Intl.DateTimeFormat(state.format, { month: 'short', day: 'numeric', timeZone }).format(at));
}

// date is a day with its year.
export function date(at: Date, timeZone?: string): string {
  return plain(new Intl.DateTimeFormat(state.format, { dateStyle: 'medium', timeZone }).format(at));
}

// integer is a whole number with the region's grouping.
export function integer(n: number): string {
  return new Intl.NumberFormat(state.format, { maximumFractionDigits: 0 }).format(n);
}

// decimal is a number with digits after the separator.
export function decimal(n: number, digits: number): string {
  return new Intl.NumberFormat(state.format, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

// list joins items as the language does, "Mike, Sam, and Dana".
export function list(items: readonly string[]): string {
  return new Intl.ListFormat(state.format, { style: 'long', type: 'conjunction' }).format(items);
}
